/**
 * Chart of Accounts — Helpers, Types, and Constants
 */

import type { JSX } from 'react'
import { createElement } from 'react'

import { Layers, Tag, DollarSign, Hash } from 'lucide-react'

import type { AccountType } from '../../services/account-service'

// ── Types ────────────────────────────────────────────────────────────────────

export interface AccountFormData {
  code: string
  name: string
  description: string
  accountType: AccountType
  accountSubtype: string
  parentId: string
  isHeader: boolean
  currency: string
  tags: string[]
  nameAtBank: string
  bankName: string
  branch: string
  accountNumber: string
  ifscCode: string
  swiftCode: string
  iban: string
  routingNumber: string
  sortCode: string
  // Reserved / GST book identity (optional; blank for ordinary accounts)
  systemTag: string
  gstComponent: string // '' | 'cgst' | 'sgst' | 'igst'
  gstDirection: string // '' | 'output' | 'input'
  gstRate: string      // '' = consolidated bucket; e.g. '9' = rate-wise leaf
}

/** Row structure for flattened tree with lazy loading */
export interface AccountRow extends Record<string, unknown> {
  account: import('../../services/account-service').Account
  depth: number
  isExpanded: boolean
  hasChildren: boolean
  hasChildrenLoaded: boolean
  isLoading: boolean
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const EMPTY_FORM_DATA: AccountFormData = {
  code: '',
  name: '',
  description: '',
  accountType: 'ACCOUNT_TYPE_ASSET',
  accountSubtype: '',
  parentId: '',
  isHeader: false,
  currency: '',
  tags: [],
  nameAtBank: '',
  bankName: '',
  branch: '',
  accountNumber: '',
  ifscCode: '',
  swiftCode: '',
  iban: '',
  routingNumber: '',
  sortCode: '',
  systemTag: '',
  gstComponent: '',
  gstDirection: '',
  gstRate: '',
}

export const ACCOUNT_TYPES: AccountType[] = [
  'ACCOUNT_TYPE_ASSET',
  'ACCOUNT_TYPE_LIABILITY',
  'ACCOUNT_TYPE_EQUITY',
  'ACCOUNT_TYPE_REVENUE',
  'ACCOUNT_TYPE_EXPENSE',
]

// ── Page info data ─────────────────────────────────────────────────────────────

interface Feature {
  icon: JSX.Element
  title: string
  description: string
}

interface Step {
  title: string
  description: string
}

export function buildCoaFeatures(): Feature[] {
  return [
    { icon: createElement(Layers, { className: 'w-4 h-4' }), title: 'Hierarchical Structure', description: 'Organize accounts into a tree with header (group) accounts and posting (leaf) accounts at any depth' },
    { icon: createElement(Tag, { className: 'w-4 h-4' }), title: 'Five Account Types', description: 'Assets, liabilities, equity, revenue, and expense accounts follow standard accounting classifications' },
    { icon: createElement(DollarSign, { className: 'w-4 h-4' }), title: 'Balance Tracking', description: 'Each posting account tracks its own balance in a designated currency with automatic base-currency conversion' },
    { icon: createElement(Hash, { className: 'w-4 h-4' }), title: 'Account Codes & Tags', description: 'Structured numeric codes enforce ordering, while custom tags enable flexible filtering and reporting' },
  ]
}

export const COA_STEPS: Step[] = [
  { title: 'Review the default accounts', description: 'Your chart of accounts comes with standard header accounts. Expand each type to see the structure.' },
  { title: 'Add posting accounts', description: 'Click "New Account" to create leaf accounts under the appropriate header for recording transactions.' },
  { title: 'Start using accounts in entries', description: 'Once your accounts are set up, select them when creating journal entries, invoices, or payments.' },
]

export const COA_WHAT_IS_IT = {
  title: 'What is the Chart of Accounts?',
  description: 'The chart of accounts is the backbone of your accounting system. It defines every account your organization uses to classify financial transactions — from bank accounts and receivables to revenue streams and expenses.',
}

// ── Utility functions ─────────────────────────────────────────────────────────

/**
 * Sort an array of account IDs by account code using the provided map.
 */
export function sortIdsByCode(
  ids: string[],
  getCode: (id: string) => string,
): string[] {
  return [...ids].sort((a, b) => getCode(a).localeCompare(getCode(b)))
}

/**
 * Determine the save-button label based on current form state.
 */
export function getSaveButtonLabel(isSaving: boolean, isEditing: boolean): string {
  if (isSaving) { return 'Saving...' }
  if (isEditing) { return 'Update' }
  return 'Create'
}

/**
 * Normalize a tag string: lowercase, replace invalid chars with underscore.
 */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_')
}

/**
 * Determine the bank-code label based on currency.
 */
export function getBankCodeLabel(currency: string): string {
  if (currency === 'INR') { return 'IFSC Code' }
  if (currency === 'GBP') { return 'Sort Code' }
  return 'SWIFT/BIC Code'
}

/**
 * Build the tab definitions used by FilterBar and onTabChange handler.
 */
export function buildTabDefinitions(): Array<{
  id: string
  conditions: Array<{ id: string; fieldId: string; operator: 'equals'; value: string }>
}> {
  return [
    { id: 'active', conditions: [{ id: 'tab-status', fieldId: 'status', operator: 'equals' as const, value: 'active' }] },
    { id: 'all', conditions: [] },
    { id: 'headers', conditions: [{ id: 'tab-kind', fieldId: 'kind', operator: 'equals' as const, value: 'header' }] },
  ]
}
